import { createElement, useId, type ComponentProps, type JSX } from 'react';

import { cn, type TIcon } from '~/utils';
import { Label } from '~/components/label';
import { Checkbox } from '~/components/checkbox';

interface ICheckboxCardProps
  extends Omit<ComponentProps<'div'>, 'ref'>,
    Pick<ComponentProps<typeof Checkbox>, 'value' | 'checked' | 'ref'> {
  icon?: TIcon;
  checkboxProps?: Omit<
    ComponentProps<typeof Checkbox>,
    'value' | 'checked' | 'ref'
  >;
  iconSize?: number;
}

export function CheckboxCard({
  ref,
  icon,
  value,
  checked,
  iconSize,
  children,
  className,
  checkboxProps,
  ...props
}: ICheckboxCardProps): JSX.Element {
  const id = useId();

  const { className: checkboxClassName, ...restCheckboxProps } =
    checkboxProps || {};

  return (
    <div className='relative' {...props}>
      <Checkbox
        ref={ref}
        value={value}
        checked={checked}
        id={value?.toString() ?? id}
        className={cn(
          'absolute inset-0 opacity-0 cursor-pointer z-50',
          checkboxClassName,
        )}
        {...restCheckboxProps}
      />
      <Label
        htmlFor={value?.toString() ?? id}
        className={cn(
          'flex flex-col text-xs font-normal leading-[130%] p-3 border w-[110px] rounded-[12px] items-center justify-center text-center gap-1.5',
          'hover:border-blueBonnet-500',
          'peer-data-[state=checked]:border-blueBonnet-500 peer-data-[state=checked]:text-blueBonnet-500 peer-data-[state=checked]:bg-blueBonnet-50 peer-data-[state=checked]:[--svg-color:var(--color-blueBonnet-500)]',
          'peer-data-[state=checked]:hover:bg-blueBonnet-100',
          className,
        )}
      >
        {icon &&
          createElement(icon, {
            size: iconSize ? `${iconSize}px` : '24px',
            className: 'shrink-0',
          })}
        {children}
      </Label>
    </div>
  );
}
